import {
  TWorkflow,
  TWorkflowStep,
  TWorkflowProject,
  TWorkflowProjectDetails,
  TWorkflowStepUserType,
} from "../../models/workflow/workflow";
import { useApiClient } from "../apiClients";
import {
  ENDPOINTS_WORKFLOWS,
  ENDPOINTS_WORKFLOW_STEPS,
  ENDPOINTS_WORKFLOW_PROJECTS,
  ENDPOINTS_WORKFLOW_PROJECT_ACTIONS,
  ENDPOINTS_WORKFLOW_STEP_USERTYPES,
} from "./endpoints";

export const useWorkflowApi = () => {
  const apiClient = useApiClient();

  // ----------------------------------------------------------------------
  // Workflows
  // ----------------------------------------------------------------------
  const getWorkflows = () =>
    apiClient.get<TWorkflow[]>(ENDPOINTS_WORKFLOWS.GET_ALL);
  const getWorkflowById = (workflowId: number) =>
    apiClient.get<TWorkflow>(ENDPOINTS_WORKFLOWS.GET_BY_ID(workflowId));
  const createWorkflow = (data: TWorkflow) =>
    apiClient.post<TWorkflow>(ENDPOINTS_WORKFLOWS.POST_WORKFLOW, data);
  const updateWorkflow = (workflowId: number, data: TWorkflow) =>
    apiClient.put<TWorkflow>(
      ENDPOINTS_WORKFLOWS.PUT_WORKFLOW(workflowId),
      data
    );
  const deleteWorkflow = (workflowId: number) =>
    apiClient.delete(ENDPOINTS_WORKFLOWS.DELETE_WORKFLOW(workflowId));

  // ----------------------------------------------------------------------
  // Workflow Steps
  // ----------------------------------------------------------------------
  const getWorkflowSteps = () =>
    apiClient.get<TWorkflowStep[]>(ENDPOINTS_WORKFLOW_STEPS.GET_ALL);
  const getWorkflowStepById = (stepId: number) =>
    apiClient.get<TWorkflowStep>(ENDPOINTS_WORKFLOW_STEPS.GET_BY_ID(stepId));
  const getWorkflowStepsByWorkflowId = (workflowId: number) =>
    apiClient.get<TWorkflowStep[]>(
      ENDPOINTS_WORKFLOW_STEPS.GET_BY_WORKFLOW_ID(workflowId)
    );
  const createWorkflowStep = (data: TWorkflowStep) =>
    apiClient.post<TWorkflowStep>(
      ENDPOINTS_WORKFLOW_STEPS.POST_WORKFLOW_STEP,
      data
    );
  const updateWorkflowStep = (stepId: number, data: TWorkflowStep) =>
    apiClient.put<TWorkflowStep>(
      ENDPOINTS_WORKFLOW_STEPS.PUT_WORKFLOW_STEP(stepId),
      data
    );
  const deleteWorkflowStep = (stepId: number) =>
    apiClient.delete(ENDPOINTS_WORKFLOW_STEPS.DELETE_WORKFLOW_STEP(stepId));

  // ----------------------------------------------------------------------
  // Workflow Projects
  // ----------------------------------------------------------------------
  const getWorkflowProjects = () =>
    apiClient.get<TWorkflowProject[]>(ENDPOINTS_WORKFLOW_PROJECTS.GET_ALL);
  const getWorkflowProjectById = (id: number) =>
    apiClient.get<TWorkflowProject>(ENDPOINTS_WORKFLOW_PROJECTS.GET_BY_ID(id));
  const getWorkflowProjectsByWorkflowId = (workflowId: number) =>
    apiClient.get<TWorkflowProject[]>(
      ENDPOINTS_WORKFLOW_PROJECTS.GET_BY_WORKFLOW_ID(workflowId)
    );
  const getWorkflowProjectsByProjectId = (projectId: number) =>
    apiClient.get<TWorkflowProject[]>(
      ENDPOINTS_WORKFLOW_PROJECTS.GET_BY_PROJECT_ID(projectId)
    );
  const createWorkflowProject = (data: TWorkflowProject) =>
    apiClient.post<TWorkflowProject>(
      ENDPOINTS_WORKFLOW_PROJECTS.POST_WORKFLOW_PROJECT,
      data
    );
  const updateWorkflowProject = (id: number, data: TWorkflowProject) =>
    apiClient.put<TWorkflowProject>(
      ENDPOINTS_WORKFLOW_PROJECTS.PUT_WORKFLOW_PROJECT(id),
      data
    );
  const deleteWorkflowProject = (id: number) =>
    apiClient.delete(ENDPOINTS_WORKFLOW_PROJECTS.DELETE_WORKFLOW_PROJECT(id));

  // ----------------------------------------------------------------------
  // Workflow Project Actions
  // ----------------------------------------------------------------------
  const getNextButton = (projectId: number) =>
    apiClient.get(
      ENDPOINTS_WORKFLOW_PROJECT_ACTIONS.GET_NEXT_BUTTON(projectId)
    );
  const createNextStep = (
    projectId: number,
    details?: TWorkflowProjectDetails
  ) =>
    apiClient.post<TWorkflowProject>(
      ENDPOINTS_WORKFLOW_PROJECT_ACTIONS.CREATE_NEXT_STEP(projectId),
      { details }
    );
  // Back step actions
  const getBackButton = (projectId: number) =>
    apiClient.get(
      ENDPOINTS_WORKFLOW_PROJECT_ACTIONS.GET_BACK_BUTTON(projectId)
    );
  const createBackStep = (
    projectId: number,
    details?: TWorkflowProjectDetails
  ) =>
    apiClient.post<TWorkflowProject>(
      ENDPOINTS_WORKFLOW_PROJECT_ACTIONS.CREATE_BACK_STEP(projectId),
      { details }
    );
  
  // ----------------------------------------------------------------------
  // Workflow Step UserType Permissions
  // ----------------------------------------------------------------------
  const getWorkflowStepUserTypes = () =>
    apiClient.get<TWorkflowStepUserType[]>(
      ENDPOINTS_WORKFLOW_STEP_USERTYPES.GET_ALL
    );
  const getWorkflowStepUserTypesByUsertypeId = (usertypeId: number) =>
    apiClient.get<TWorkflowStepUserType[]>(
      ENDPOINTS_WORKFLOW_STEP_USERTYPES.GET_BY_USERTYPE_ID(usertypeId)
    );
  
  const getWorkflowStepUserTypesByStepId = (stepId: number) =>
    apiClient.get<TWorkflowStepUserType[]>(
      ENDPOINTS_WORKFLOW_STEP_USERTYPES.GET_BY_STEP_ID(stepId)
    );
  
  const createWorkflowStepUserType = (data: TWorkflowStepUserType) =>
    apiClient.post<TWorkflowStepUserType>(
      ENDPOINTS_WORKFLOW_STEP_USERTYPES.CREATE,
      data
    );
  const updateWorkflowStepUserType = (
    id: number,
    data: TWorkflowStepUserType
  ) =>
    apiClient.put<TWorkflowStepUserType>(
      ENDPOINTS_WORKFLOW_STEP_USERTYPES.UPDATE(id),
      data
    );
  const deleteWorkflowStepUserType = (id: number) =>
    apiClient.delete(
      ENDPOINTS_WORKFLOW_STEP_USERTYPES.DELETE(id)
    );

  return {
    getWorkflows,
    getWorkflowById,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,

    getWorkflowSteps,
    getWorkflowStepById,
    getWorkflowStepsByWorkflowId,
    createWorkflowStep,
    updateWorkflowStep,
    deleteWorkflowStep,

    getWorkflowProjects,
    getWorkflowProjectById,
    getWorkflowProjectsByWorkflowId,
    getWorkflowProjectsByProjectId,
    createWorkflowProject,
    updateWorkflowProject,
    deleteWorkflowProject,

    getNextButton,
    createNextStep,
    getBackButton,
    createBackStep,

    getWorkflowStepUserTypes,
    getWorkflowStepUserTypesByUsertypeId,
    createWorkflowStepUserType,
    updateWorkflowStepUserType,
    deleteWorkflowStepUserType,
    getWorkflowStepUserTypesByStepId,
  };
};
